import { useState, useEffect } from 'react';
import { X, Shield, Check } from 'lucide-react';
import api from '../api/config';
import './PermissionsModal.css';

const PermissionsModal = ({ isOpen, onClose, member, onSave, currentUserRole, isSuperAdmin }) => {
  const [permissions, setPermissions] = useState({
    sections: {
      dashboard: true,
      maestro: false,
      clientes: false,
      proveedores: false,
      equipo: false,
      empresa: false,
      historial: false
    }
  });
  const [originalPermissions, setOriginalPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showMessage, setShowMessage] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Verificar si el usuario puede editar permisos (solo admin, editor o superadmin)
  const canEditPermissions = isSuperAdmin || currentUserRole === 'admin' || currentUserRole === 'editor';

  const sectionLabels = {
    dashboard: 'Dashboard',
    maestro: 'Maestro',
    clientes: 'Clientes',
    proveedores: 'Proveedores',
    equipo: 'Equipo',
    empresa: 'Empresa',
    historial: 'Historial'
  };

  const sectionDescriptions = {
    dashboard: 'Panel principal con estadísticas',
    maestro: 'Tabla Maestro de órdenes de compra',
    clientes: 'Gestión de clientes',
    proveedores: 'Gestión de proveedores',
    equipo: 'Gestión del equipo de trabajo',
    empresa: 'Configuración de la empresa',
    historial: 'Historial de actividades del sistema'
  };

  useEffect(() => {
    if (member && member.permissions) {
      setPermissions(member.permissions);
      setOriginalPermissions(JSON.parse(JSON.stringify(member.permissions)));
      setHasChanges(false);
    }
  }, [member]);

  useEffect(() => {
    if (originalPermissions) {
      const changed = JSON.stringify(permissions) !== JSON.stringify(originalPermissions);
      setHasChanges(changed);
    }
  }, [permissions, originalPermissions]);

  const handleToggleSection = (section) => {
    if (!canEditPermissions) return; // No permitir cambios si no tiene permisos

    setPermissions(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: !prev.sections[section]
      }
    }));
  };

  const handleSave = async () => {
    if (!hasChanges) return;

    setLoading(true);
    try {
      await onSave(member.id, permissions);
      setMessage({ type: 'success', text: 'Permisos actualizados exitosamente' });
      setShowMessage(true);
      setTimeout(() => {
        onClose();
        setShowMessage(false);
        setMessage({ type: '', text: '' });
      }, 2000);
    } catch (error) {
      console.error('Error saving permissions:', error);
      setMessage({ type: 'error', text: 'Error al guardar permisos' });
      setShowMessage(true);
      setTimeout(() => {
        setShowMessage(false);
        setMessage({ type: '', text: '' });
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !member) return null;

  return (
    <div className="permissions-modal-overlay" onClick={onClose}>
      <div className="permissions-modal" onClick={(e) => e.stopPropagation()}>
        <div className="permissions-modal-header">
          <div className="header-info">
            <Shield size={24} />
            <div>
              <h2>Permisos de {member.nombre}</h2>
              <p>Configura el acceso a las diferentes secciones del sistema</p>
            </div>
          </div>
          <button className="permissions-modal-close" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {showMessage ? (
          <div className={`permissions-message ${message.type}`}>
            {message.type === 'success' ? (
              <div className="message-content">
                <Check size={64} />
                <h3>{message.text}</h3>
              </div>
            ) : (
              <div className="message-content">
                <X size={64} />
                <h3>{message.text}</h3>
              </div>
            )}
          </div>
        ) : (
        <div className="permissions-content">
          {!canEditPermissions && (
            <p style={{
              color: '#dc2626',
              fontSize: '14px',
              fontWeight: '600',
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: '#fee2e2',
              borderRadius: '8px',
              border: '1px solid #dc2626'
            }}>
              ⚠️ No tienes permisos para modificar los permisos de otros usuarios
            </p>
          )}
          <div className="permissions-grid">
            {Object.entries(sectionLabels).map(([key, label]) => (
              <div key={key} className="permission-item">
                <div
                  className={`permission-card ${permissions.sections[key] ? 'active' : ''}`}
                  onClick={() => handleToggleSection(key)}
                  style={{
                    opacity: !canEditPermissions ? 0.5 : 1,
                    cursor: !canEditPermissions ? 'not-allowed' : 'pointer'
                  }}
                >
                  <div className="permission-checkbox">
                    {permissions.sections[key] && <Check size={16} />}
                  </div>
                  <div className="permission-info">
                    <h4>{label}</h4>
                    <p>{sectionDescriptions[key]}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {!showMessage && (
        <div className="permissions-modal-footer">
          <button
            className="btn-cancel-permissions"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className={`btn-save-permissions ${!hasChanges || !canEditPermissions ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={loading || !hasChanges || !canEditPermissions}
            title={!canEditPermissions ? 'No tienes permisos para modificar esto' : (!hasChanges ? 'No hay cambios para guardar' : '')}
          >
            {loading ? 'Guardando...' : 'Guardar Permisos'}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default PermissionsModal;